import OnboardingDialog from '@rio-cloud/rio-uikit/OnboardingDialog';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import onboardingImage from '../../../src/assets/onboarding/onboarding.webp';
import { useOnboardingTourContext } from '../../components/OnboardingTour.context';
import { useIsServiceInfoDialogShown } from '../serviceInfo/serviceInfoSelectors';
import type { RentalOnboardingStep } from './onboardingUtils';
import { useOnboarding } from '../../hooks/useOnboarding';

type PanelProps = {
    step?: RentalOnboardingStep;
    className?: string;
    onActivated?: () => void;
    onDeactivated?: () => void;
};

export const OnboardingPanel = ({
    children,
    step,
    className = '',
    onActivated,
    onDeactivated,
}: PropsWithChildren<PanelProps>) => {
    const { activeStep } = useOnboardingTourContext();

    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        const isActiveStep = step === activeStep;
        if (isActiveStep && !isActive) {
            onActivated?.();
            setIsActive(isActive => !isActive);
        } else if (!isActiveStep && isActive) {
            onDeactivated?.();
            setIsActive(isActive => !isActive);
        }
    }, [activeStep, isActive, onActivated, onDeactivated, step]);

    return (
        <div className={className} data-onboarding-walkthrough-step={step}>
            {children}
        </div>
    );
};

type RentalOnboardingTourProps = {
    showAutomatically: boolean;
    showPartnerOnboardingBanner: boolean;
};

export const RentalOnboardingTour = ({ showAutomatically }: RentalOnboardingTourProps) => {
    const intl = useIntl();

    const { run } = useOnboarding();

    const { showRentalOnboardingTour, setShowRentalOnboardingTour } = useOnboardingTourContext();

    const [hasOnboardingTourBeenSeen, setHasOnboardingTourBeenSeen] = useLocalStorage<boolean>(
        'hasRentalOnboardingTourBeenSeen',
        false
    );

    const isServiceInfoDialogShown = useIsServiceInfoDialogShown();

    const showOnboardingDialog =
        (showRentalOnboardingTour || (showAutomatically && !hasOnboardingTourBeenSeen)) && !isServiceInfoDialogShown;

    const closeOnboardingDialog = () => {
        setHasOnboardingTourBeenSeen(true);
        setShowRentalOnboardingTour(false);
    };

    const onStartOnboardingTour = () => {
        closeOnboardingDialog();
        run();
    };

    const onCloseOnboardingTour = () => {
        closeOnboardingDialog();
    };

    return (
        <OnboardingDialog
            show={showOnboardingDialog}
            title={intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.welcome.title' })}
            description={intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.welcome.description' })}
            onboardingRestartHint={intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.welcome.not-ready.title' })}
            onboardingRestartDescription={intl.formatMessage({
                id: 'intl-msg:rental-web.onboarding.welcome.not-ready.description',
            })}
            tourButtonText={intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.welcome.start-tour.button' })}
            skipButtonText={intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.welcome.skip.button' })}
            imageSrc={onboardingImage}
            onStartTour={() => onStartOnboardingTour()}
            onClose={onCloseOnboardingTour}
        />
    );
};
